import { useMutation } from '@apollo/client';
import type { UpdateObligationOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateObligationOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateObligationOversightResultInput } from './useUpdateObligationOversightResultTRPC';
import { useUpdateObligationOversightResultTRPC } from './useUpdateObligationOversightResultTRPC';

export const useUpdateObligationOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateObligationOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'obligation_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateObligationOversightResultTRPC();

  const updateObligationOversightResult = async (
    variables: UpdateObligationOversightResultInput
  ): Promise<UpdateObligationOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateObligationOversightResult(variables);
    }

    const result = await updateGraphQL({
      variables: {
        Id: variables.Id,
        Rating: variables.Rating,
        Rationale: variables.Rationale,
        TestDate: variables.TestDate,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update obligation oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateObligationOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateObligationOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
